import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { FileText, Plus, Pencil, Trash2, Upload, Check, Building2, CreditCard, Image, Save, Eye, FileDown, X } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const TEMPLATE_TYPES = [
  { value: 'invoice', label: 'Invoice', icon: FileText, color: 'bg-blue-500' },
  { value: 'boq', label: 'Bill of Quantities', icon: FileText, color: 'bg-green-500' },
  { value: 'quotation', label: 'Quotation', icon: FileText, color: 'bg-purple-500' },
  { value: 'purchase_order', label: 'Purchase Order', icon: FileText, color: 'bg-orange-500' },
  { value: 'payment_receipt', label: 'Payment Receipt', icon: FileText, color: 'bg-teal-500' },
  { value: 'checklist', label: 'Project Checklist', icon: FileText, color: 'bg-rose-500' },
];

export default function PDFTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('invoice');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    template_type: 'invoice',
    name: '',
    company_name: '',
    tagline: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    gst_number: '',
    pan_number: '',
    bank_name: '',
    account_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    upi_id: '',
    primary_color: '#1c1c1e',
    secondary_color: '#C5A572',
    header_text: '',
    footer_text: '',
    terms_conditions: '',
    notes: '',
    is_default: false,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/pdf-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = (type) => {
    setFormData({
      ...formData,
      template_type: type,
      name: `${type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Template`,
    });
    setEditingTemplate(null);
    setEditDialogOpen(true);
  };

  const handleEdit = (template) => {
    setFormData({ ...formData, ...template });
    setEditingTemplate(template);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingTemplate?.id) {
        await axios.put(`${API}/pdf-templates/${editingTemplate.id}`, formData);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/pdf-templates`, formData);
        toast.success('Template created');
      }
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API}/pdf-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file || !editingTemplate?.id) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/pdf-templates/${editingTemplate.id}/upload-image?image_type=${imageType}`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFormData({ ...formData, [`${imageType}_url`]: response.data.image_url });
      toast.success(`${imageType} uploaded`);
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const filteredTemplates = templates.filter(t => t.template_type === selectedType);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-manrope">PDF Templates</h1>
            <p className="text-stone-500">Customize templates for Invoice, BOQ, Quotation, Purchase Orders & Receipts</p>
          </div>
        </div>

        {/* Template Type Tabs */}
        <Tabs value={selectedType} onValueChange={setSelectedType}>
          <TabsList className="bg-stone-100 p-1 rounded-xl flex-wrap h-auto gap-1">
            {TEMPLATE_TYPES.map(type => (
              <TabsTrigger 
                key={type.value} 
                value={type.value}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4"
              >
                <type.icon size={16} className="mr-2" />
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {TEMPLATE_TYPES.map(type => (
            <TabsContent key={type.value} value={type.value} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Create New Card */}
                <button
                  onClick={() => handleCreateNew(type.value)}
                  className="apple-card p-6 border-2 border-dashed border-stone-300 hover:border-stone-400 hover:bg-stone-50 transition-all flex flex-col items-center justify-center gap-3 min-h-[200px]"
                  data-testid={`create-${type.value}-template`}
                >
                  <div className={`p-3 rounded-xl ${type.color} text-white`}>
                    <Plus size={24} />
                  </div>
                  <span className="font-medium text-stone-700">Create New {type.label} Template</span>
                </button>

                {/* Existing Templates */}
                {filteredTemplates.map(template => (
                  <div key={template.id} className="apple-card p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${type.color} text-white`}>
                          <type.icon size={20} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900">{template.name}</h3>
                          <p className="text-sm text-stone-500">{template.company_name || 'No company set'}</p>
                        </div>
                      </div>
                      {template.is_default && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Default</span>
                      )}
                    </div>

                    {/* Preview Info */}
                    <div className="space-y-2 text-sm text-stone-600">
                      {template.bank_name && (
                        <div className="flex items-center gap-2">
                          <CreditCard size={14} />
                          <span>{template.bank_name}</span>
                        </div>
                      )}
                      {template.logo_url && (
                        <div className="flex items-center gap-2">
                          <Image size={14} />
                          <span>Logo uploaded</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(template)} className="flex-1">
                        <Pencil size={14} className="mr-2" />
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(template.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}

                {filteredTemplates.length === 0 && (
                  <div className="col-span-2 text-center py-8 text-stone-500">
                    No templates created for {type.label} yet
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Edit/Create Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <FileText size={20} />
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="company" className="mt-4">
              <TabsList className="w-full">
                <TabsTrigger value="company" className="flex-1">
                  <Building2 size={14} className="mr-2" />
                  Company
                </TabsTrigger>
                <TabsTrigger value="banking" className="flex-1">
                  <CreditCard size={14} className="mr-2" />
                  Banking
                </TabsTrigger>
                <TabsTrigger value="images" className="flex-1">
                  <Image size={14} className="mr-2" />
                  Images
                </TabsTrigger>
                <TabsTrigger value="content" className="flex-1">
                  <FileText size={14} className="mr-2" />
                  Content
                </TabsTrigger>
              </TabsList>

              {/* Company Info Tab */}
              <TabsContent value="company" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Template Name *</Label>
                    <Input 
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="My Invoice Template"
                    />
                  </div>
                  <div>
                    <Label>Template Type</Label>
                    <Select value={formData.template_type} onValueChange={(v) => setFormData({...formData, template_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input 
                      value={formData.company_name} 
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Bella Casa Interiors"
                    />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input 
                      value={formData.tagline} 
                      onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                      placeholder="Crafting Dream Spaces"
                    />
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Textarea 
                    value={formData.address} 
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Design Street, Creative District"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input 
                      value={formData.city} 
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Mumbai"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={formData.phone} 
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input 
                      value={formData.email} 
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="info@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Website</Label>
                    <Input 
                      value={formData.website} 
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="www.company.com"
                    />
                  </div>
                  <div>
                    <Label>GST Number</Label>
                    <Input 
                      value={formData.gst_number} 
                      onChange={(e) => setFormData({...formData, gst_number: e.target.value})}
                      placeholder="27XXXXX1234X1ZX"
                    />
                  </div>
                  <div>
                    <Label>PAN Number</Label>
                    <Input 
                      value={formData.pan_number} 
                      onChange={(e) => setFormData({...formData, pan_number: e.target.value})}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="color"
                        value={formData.primary_color} 
                        onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                        className="w-12 h-10 p-1"
                      />
                      <Input 
                        value={formData.primary_color} 
                        onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                        placeholder="#1c1c1e"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Secondary Color</Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        type="color"
                        value={formData.secondary_color} 
                        onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                        className="w-12 h-10 p-1"
                      />
                      <Input 
                        value={formData.secondary_color} 
                        onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                        placeholder="#C5A572"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <input 
                    type="checkbox" 
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="is_default" className="cursor-pointer">Set as default template for this type</Label>
                </div>
              </TabsContent>

              {/* Banking Tab */}
              <TabsContent value="banking" className="space-y-4 mt-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">Banking details will appear on BOQ, Quotation, and Invoice PDFs</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input 
                      value={formData.bank_name} 
                      onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                      placeholder="HDFC Bank"
                    />
                  </div>
                  <div>
                    <Label>Account Holder Name</Label>
                    <Input 
                      value={formData.account_name} 
                      onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                      placeholder="Bella Casa Interiors Pvt Ltd"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Account Number</Label>
                    <Input 
                      value={formData.account_number} 
                      onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                      placeholder="1234567890123"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input 
                      value={formData.ifsc_code} 
                      onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                      placeholder="HDFC0001234"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Branch</Label>
                    <Input 
                      value={formData.branch} 
                      onChange={(e) => setFormData({...formData, branch: e.target.value})}
                      placeholder="Andheri West Branch"
                    />
                  </div>
                  <div>
                    <Label>UPI ID</Label>
                    <Input 
                      value={formData.upi_id} 
                      onChange={(e) => setFormData({...formData, upi_id: e.target.value})}
                      placeholder="company@upi"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Images Tab */}
              <TabsContent value="images" className="space-y-4 mt-4">
                {!editingTemplate?.id && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700">Save the template first to upload images</p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  {/* Logo Upload */}
                  <div className="border rounded-lg p-4 text-center space-y-3">
                    <div className="w-24 h-24 mx-auto bg-stone-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {formData.logo_url ? (
                        <img src={`${API.replace('/api', '')}${formData.logo_url}`} alt="Logo" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <Image size={32} className="text-stone-400" />
                      )}
                    </div>
                    <p className="font-medium text-sm">Company Logo</p>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUpload(e, 'logo')}
                      disabled={!editingTemplate?.id}
                      className="text-sm w-full"
                    />
                  </div>

                  {/* Signature Upload */}
                  <div className="border rounded-lg p-4 text-center space-y-3">
                    <div className="w-24 h-24 mx-auto bg-stone-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {formData.signature_url ? (
                        <img src={`${API.replace('/api', '')}${formData.signature_url}`} alt="Signature" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <Pencil size={32} className="text-stone-400" />
                      )}
                    </div>
                    <p className="font-medium text-sm">Signature</p>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUpload(e, 'signature')}
                      disabled={!editingTemplate?.id}
                      className="text-sm w-full"
                    />
                  </div>

                  {/* Stamp Upload */}
                  <div className="border rounded-lg p-4 text-center space-y-3">
                    <div className="w-24 h-24 mx-auto bg-stone-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {formData.stamp_url ? (
                        <img src={`${API.replace('/api', '')}${formData.stamp_url}`} alt="Stamp" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <Check size={32} className="text-stone-400" />
                      )}
                    </div>
                    <p className="font-medium text-sm">Company Stamp</p>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUpload(e, 'stamp')}
                      disabled={!editingTemplate?.id}
                      className="text-sm w-full"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-4 mt-4">
                <div>
                  <Label>Header Text</Label>
                  <Textarea 
                    value={formData.header_text} 
                    onChange={(e) => setFormData({...formData, header_text: e.target.value})}
                    placeholder="Custom header text for your PDF..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Terms & Conditions</Label>
                  <Textarea 
                    value={formData.terms_conditions} 
                    onChange={(e) => setFormData({...formData, terms_conditions: e.target.value})}
                    placeholder="1. Payment terms: 50% advance, 50% on completion&#10;2. Prices are valid for 30 days..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label>Additional Notes</Label>
                  <Textarea 
                    value={formData.notes} 
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Thank you for your business!"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Footer Text</Label>
                  <Textarea 
                    value={formData.footer_text} 
                    onChange={(e) => setFormData({...formData, footer_text: e.target.value})}
                    placeholder="This is a computer generated document..."
                    rows={2}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} className="apple-button">
                <Save size={16} className="mr-2" />
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
